import "./App.css";

import { HashRouter as Router, Navigate, Route, Routes } from "react-router-dom";

import {
  FluentProvider,
  teamsDarkTheme,
  teamsHighContrastTheme,
  teamsLightTheme,
} from "@fluentui/react-components";

import { AppThemeProvider, useAppTheme } from "./theme/AppTheme";
import Privacy from "./Privacy";
import TabConfig from "./TabConfig";
import TermsOfUse from "./TermsOfUse";
import MainDashboard from "./dashboards/MainDashboard";
import AssistantDashboard from "./dashboards/AssistantDashboard";
import { ReactNode, useEffect, useState } from "react";

import { initializeTeams } from "./TeamsHost";
import { ToastProvider } from "./components/ToastProvider";
import { ConfirmProvider } from "./components/ConfirmProvider";
import { CreateUser } from "./services/UserSettingService";
import FactoryView from "./FactoryOverview/FactoryView";
import { PilotDrawerProvider } from "./contexts/PilotDrawerContext";
import OiView from "./components/OI/OiView";
import StopCodingDashboard from "./dashboards/StopCodingDashboard";
import { TourProvider } from "./contexts/TourContext";
import OiFileGalleryModal from "./components/OI/OiFileGalleryModal";
import { useAppDispatch, useAppSelector } from "./app/hooks";
import { closeFileGallery } from "./Slices/FileGallerySlice";
import { getTeamsUser } from "./Sso";
import { installApiAuthInterceptor, setAuthToken, setTokenProvider } from "./services/ApiAuth";
import { getBrowserAccessToken, signInWithBrowser } from "./BrowserAuth";
import { getUserFromJwt, SignedInUser } from "./services/JwtUser";
import { setDatabaseUserId, setSignedInUser } from "./services/CurrentUser";
import StopCodingDashboardNew from "./dashboards/StopCodingDashboardNew";
import { StopCodingProvider } from "./components/OI/redux/ReduxContext";
import StopCodingView from "./dashboards/StopCodingView";
import WebTabNavigation from "./components/WebTabNavigation";
import Modal from "./components/Modal";
import { UserPreferencesProvider } from "./contexts/UserPreferencesContext";
import RoleOnboardingDialog from "./components/Settings/RoleOnboardingDialog";
import UserSettingsDialog from "./components/Settings/UserSettingsDialog";
import {
  buildServiceStatusFailureState,
  buildServiceStatusPopupState,
  getServiceStatus,
  ServiceStatusPopupState,
} from "./services/ServiceStatusService";

installApiAuthInterceptor();

export { userId, email, fullName, id } from "./services/CurrentUser";

const getBrowserUser = async (): Promise<SignedInUser | null> => {
  const browserUser = await signInWithBrowser();
  if (!browserUser) {
    // Release API calls waiting for a token instead of letting each one hit the wait timeout.
    setAuthToken(null);
    return null;
  }

  setTokenProvider(getBrowserAccessToken);
  const accessToken = await getBrowserAccessToken();
  setAuthToken(accessToken);
  // Same identity the API sees; falls back to the ID token when no API scope is configured.
  return getUserFromJwt(accessToken) ?? browserUser;
};

// Registers the user in the HowRU database; the returned id is required for user settings.
const registerUser = async (user: SignedInUser): Promise<void> => {
  const createdUserId = await CreateUser({
    userName: user.displayName,
    email: user.email,
    fullName: user.displayName,
  });

  if (!createdUserId) {
    console.error("Failed to register the signed-in user; user settings cannot be loaded or saved.");
    return;
  }

  setDatabaseUserId(createdUserId);
};

const loadSignedInUser = async (): Promise<void> => {
  const inTeams = await initializeTeams();
  const currentUser = inTeams ? await getTeamsUser() : await getBrowserUser();
  if (!currentUser) {
    console.error(`No signed-in user could be resolved (${inTeams ? "Teams" : "browser"}); user settings are unavailable.`);
    return;
  }

  setSignedInUser(currentUser);
  await registerUser(currentUser);
};

const fluentThemes = {
  default: teamsLightTheme,
  dark: teamsDarkTheme,
  contrast: teamsHighContrastTheme,
};

function ThemedFluentProvider({ children }: { children: ReactNode }) {
  const { themeString } = useAppTheme();

  return (
    <FluentProvider id="fluent-provider" theme={fluentThemes[themeString]}>
      {children}
    </FluentProvider>
  );
}

export default function App() {
  const [serviceStatusPopup, setServiceStatusPopup] = useState<ServiceStatusPopupState>({
    isOpen: false,
    affectedServices: [],
    isGenericFailure: false,
  });
  const dispatch = useAppDispatch();
  const fileGallery = useAppSelector((state) => state.fileGallerySlice);

  useEffect(() => {
    loadSignedInUser().catch((error) => console.error("Failed to load the signed-in user:", error));

    getServiceStatus()
      .then((statuses) => setServiceStatusPopup(buildServiceStatusPopupState(statuses)))
      .catch((error) => {
        console.error("Failed to fetch service status", error);
        setServiceStatusPopup(buildServiceStatusFailureState());
      });
  }, []);

  const closeServiceStatusPopup = () => {
    setServiceStatusPopup((currentState) => ({
      ...currentState,
      isOpen: false,
    }));
  };






  return (
    <AppThemeProvider>
      <ThemedFluentProvider>
        <UserPreferencesProvider>
          <TourProvider>
            <PilotDrawerProvider>
              <ToastProvider>
                <ConfirmProvider>
                  <Router>
                    <WebTabNavigation />
                    <RoleOnboardingDialog />
                    <UserSettingsDialog />
                    <Routes>
                      <Route path="/privacy" element={<Privacy />} />
                      <Route path="/termsofuse" element={<TermsOfUse />} />
                      <Route path="/tab" element={<MainDashboard />} />
                      <Route path="/Assistant" element={<AssistantDashboard />} />
                      <Route path="/config" element={<TabConfig />} />
                      <Route path="/Factory" element={<FactoryView />} />
                      <Route path="/Oi/*" element={<OiView />} />
                      <Route path="/Factory/:machineId" element={<FactoryView />} />
                      <Route path="/StopCoding" element={<StopCodingView />} />
                      <Route path="*" element={<Navigate to={"/tab"} />} />
                    </Routes>
                  </Router>

                  <OiFileGalleryModal
                    isOpen={fileGallery.isOpen}
                    onClose={() => dispatch(closeFileGallery())}
                    files={fileGallery.files}
                    initialIndex={fileGallery.initialIndex}
                    title={fileGallery.title}
                  />

                  <Modal
                    isOpen={serviceStatusPopup.isOpen}
                    onClose={closeServiceStatusPopup}
                    title="Service Status Update"
                    width="100"
                  >
                    <div className="space-y-4">
                      <p>Some services are currently experiencing issues.</p>
                      {serviceStatusPopup.affectedServices.length > 0 && (
                        <div>
                          <p className="font-semibold">Services affected:</p>
                          <ul className="list-disc pl-6">
                            {serviceStatusPopup.affectedServices.map((serviceName) => (
                              <li key={serviceName}>{serviceName}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {serviceStatusPopup.isGenericFailure && (
                        <p>
                          We could not confirm the latest service health status right now.
                        </p>
                      )}
                      <p>You may experience disruptions in functionality.</p>
                      <p>
                        We are working to resolve the issues as quickly as possible. Thank you for your patience.
                      </p>
                    </div>
                  </Modal>

                  <a
                    href="https://forms.office.com/e/MMkwf4DYzZ"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-28 h-6 text-center fixed bottom-20 -right-11 -rotate-90"
                  >
                    <button className="w-full h-full flex items-center justify-center text-text-light dark:text-text-dark text-base ">
                      Feedback
                    </button>
                  </a>
                </ConfirmProvider>
              </ToastProvider>
            </PilotDrawerProvider>
          </TourProvider>
        </UserPreferencesProvider>
      </ThemedFluentProvider>
    </AppThemeProvider>
  );
}